import React, { useState, useEffect } from 'react';
import { Topic, RatingSubmission } from '../types/index.ts';
import { topicApi } from '../services/api.ts';
import { authService } from '../services/auth.ts';
import TruncatedDescription from './TruncatedDescription.tsx';
import PresentersList from './PresentersList.tsx';

interface RatingModalProps {
  topic: Topic;
  onClose: () => void;
  onRatingSubmitted: () => void;
}

const RatingModal: React.FC<RatingModalProps> = ({ topic, onClose, onRatingSubmitted }) => {
  const [usefulnessRating, setUsefulnessRating] = useState<number | undefined>();
  const [recommendationRating, setRecommendationRating] = useState<number | undefined>();
  const [customerConversations, setCustomerConversations] = useState<string>('');
  const [hoursSaved, setHoursSaved] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [hasExistingRating, setHasExistingRating] = useState(false);

  const userInfo = authService.getUserInfo();
  const userAlias = userInfo?.username || '';

  useEffect(() => {
    const loadExistingRating = async () => {
      if (!userAlias) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await topicApi.getRatings(topic.topicId);
        const userRating = response.find((rating: any) => rating.userId === userAlias);
        
        if (userRating) {
          setUsefulnessRating(userRating.usefulnessRating);
          setRecommendationRating(userRating.recommendationRating);
          setCustomerConversations(userRating.customerConversations?.toString() || '');
          setHoursSaved(userRating.hoursSaved?.toString() || '');
          setHasExistingRating(true);
        }
      } catch (error) {
        console.error('Failed to load existing rating:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingRating();
  }, [topic.topicId, userAlias]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userAlias) {
      setError('User not authenticated');
      return;
    }

    const conversationsCount = customerConversations.trim() ? parseInt(customerConversations.trim()) : undefined;
    const hoursCount = hoursSaved.trim() ? parseFloat(hoursSaved.trim()) : undefined;
    
    if (conversationsCount !== undefined && (isNaN(conversationsCount) || conversationsCount < 0)) {
      setError('Customer conversations must be a valid number (0 or greater)');
      return;
    }

    if (hoursCount !== undefined && (isNaN(hoursCount) || hoursCount < 0)) {
      setError('Hours saved must be a valid number (0 or greater)');
      return;
    }

    if (!usefulnessRating && !recommendationRating && conversationsCount === undefined && hoursCount === undefined) {
      setError('Please provide at least one rating, conversation count, or hours saved');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const rating: RatingSubmission = {
        userAlias: userAlias,
        usefulnessRating,
        recommendationRating,
        customerConversations: conversationsCount,
        hoursSaved: hoursCount
      };

      const response = await topicApi.submitRating(topic.topicId, rating);
      
      if (response.status === 'conflict') {
        setShowUpdateConfirm(true);
        setIsSubmitting(false);
        return;
      }

      onRatingSubmitted();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to submit rating');
      setIsSubmitting(false);
    }
  };

  const handleUpdateRating = async () => {
    setIsSubmitting(true);
    try {
      const conversationsCount = customerConversations.trim() ? parseInt(customerConversations.trim()) : undefined;
      const hoursCount = hoursSaved.trim() ? parseFloat(hoursSaved.trim()) : undefined;
      
      const rating: RatingSubmission = {
        userAlias: userAlias,
        usefulnessRating,
        recommendationRating,
        customerConversations: conversationsCount,
        hoursSaved: hoursCount
      };

      await topicApi.updateRating(topic.topicId, rating);
      onRatingSubmitted();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update rating');
      setIsSubmitting(false);
    }
  };

  const handleDeleteRating = async () => {
    if (!window.confirm('Are you sure you want to delete your feedback for this topic? This action cannot be undone.')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await topicApi.deleteRating(topic.topicId, userAlias);
      onRatingSubmitted();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete rating');
      setIsSubmitting(false);
    }
  };

  const renderStarRating = (
    value: number | undefined, 
    onChange: (rating: number | undefined) => void,
    label: string
  ) => (
    <div className="rating-group">
      <label>{label}:</label>
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star ${value && value >= star ? 'filled' : ''}`}
            onClick={() => onChange(star)}
          >
            ★
          </button>
        ))}
        {value && (
          <button
            type="button"
            className="clear-rating"
            onClick={() => onChange(undefined)}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Not presented yet';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (showUpdateConfirm) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <h3>Update Existing Rating</h3>
          <p>You've already rated this topic. Update your existing rating?</p>
          <div className="modal-actions">
            <button onClick={handleUpdateRating} disabled={isSubmitting}>
              Update Rating
            </button>
            <button onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Feedback</h3>
        <p style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontSize: '14px', color: '#666', margin: '0 0 15px 0' }}>
          Providing feedback as <span style={{ color: '#2563eb', fontWeight: '600' }}>{authService.getUserInfo()?.name || 'Unknown User'}</span>
        </p>
        {(usefulnessRating > 0 || recommendationRating > 0 || customerConversations > 0 || hoursSaved > 0) && (
          <p style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontSize: '14px', color: '#666', margin: '0 0 15px 0' }}>
            (you already provided feedback but feel free to modify it!)
          </p>
        )}
        <div className="topic-details">
          <p>Topic: <strong>{topic.title}</strong></p>
          <p>Description: <strong><TruncatedDescription description={topic.description || 'No description provided'} topicId={topic.topicId} /></strong></p>
          <p>Presenter(s): <strong>
            <PresentersList 
              presenters={topic.presenters || []} 
              currentUser={authService.getUserInfo()?.username}
              displayMode="inline"
            />
          </strong></p>
          <p>Presented to: <strong>{topic.teamName}</strong></p>
          <p>Date Presented: <strong>{formatDate(topic.presentedAt)}</strong></p>
        </div>
        
        <form onSubmit={handleSubmit}>
          {renderStarRating(
            usefulnessRating,
            setUsefulnessRating,
            'How much did this topic boost your confidence or knowledge? (optional)'
          )}

          {renderStarRating(
            recommendationRating,
            setRecommendationRating,
            'How likely are you to recommend a colleague watch a recording or read the summary of this topic? (optional)'
          )}

          <div style={{ borderTop: '1px solid #e5e7eb', margin: '20px 0 15px 0' }}></div>

          <p style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontSize: '14px', color: '#666', margin: '0 0 10px 0', fontWeight: '500' }}>
            You can modify or add this feedback later:
          </p>

          <div className="form-group">
            <label>Roughly how many hours of research or preparation did this topic save you? (optional):</label>
            <input
              type="number"
              step="0.25"
              value={hoursSaved}
              onChange={(e) => setHoursSaved(e.target.value)}
              placeholder="Enter in hours, ie: 2.5 or 0.5"
              min="0"
              className="hours-input"
            />
          </div>

          <div className="form-group">
            <label>How many customers did this topic help you speak with? (optional):</label>
            <input
              type="number"
              value={customerConversations}
              onChange={(e) => setCustomerConversations(e.target.value)}
              placeholder="Enter number of customers"
              min="0"
              className="customers-input"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {hasExistingRating && (
                <button 
                  type="button" 
                  onClick={handleDeleteRating}
                  disabled={isSubmitting}
                  style={{ 
                    backgroundColor: '#dc2626', 
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Delete My Feedback
                </button>
              )}
            </div>
            <div>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RatingModal;
